import type { ReactNode } from 'react';
import { AlertTriangle, Clock, X, Zap } from 'lucide-react';
import type { SubscriptionState } from '@/hooks/useSubscription';
import { cn } from '@/lib/utils';

interface TrialBannerProps {
    subscription: SubscriptionState;
    onDismiss?: () => void;
    onUpgrade?: () => void;
}

export function TrialBanner({ subscription, onDismiss, onUpgrade }: TrialBannerProps): ReactNode {
    const { urgency, daysLeft } = subscription;

    if (urgency === 'none' || urgency === 'expired') return null;

    const configs = {
        info: {
            bg: 'bg-[#EFF6FF] border-[#BFDBFE]',
            text: 'text-[#1D4ED8]',
            icon: <Clock className="h-4 w-4 shrink-0" />,
            message: `Il vous reste ${daysLeft} jours d'essai gratuit.`,
            cta: 'Voir les offres, dès 39€/mois',
        },
        warning: {
            bg: 'bg-[#FFFBEB] border-[#FDE68A]',
            text: 'text-[#92400E]',
            icon: <AlertTriangle className="h-4 w-4 shrink-0" />,
            message: daysLeft === 1
                ? "⚡ Votre essai expire demain ! Ne perdez pas l'accès à vos données."
                : `⚡ Plus que ${daysLeft} jours ! Continuez sans interruption.`,
            cta: 'Continuer, dès 39€/mois',
        },
        critical: {
            bg: 'bg-[#FEF2F2] border-[#FECACA]',
            text: 'text-[#991B1B]',
            icon: <Zap className="h-4 w-4 shrink-0 fill-current" />,
            message: daysLeft !== null && daysLeft <= 0
                ? '🚨 Votre essai expire aujourd\'hui ! Souscrivez maintenant pour ne pas perdre vos données.'
                : `🚨 Dernier jour ! Votre essai expire aujourd'hui.`,
            cta: 'Sécuriser, dès 39€/mois',
        },
    };

    const config = configs[urgency];

    return (
        <div className={cn(
            'flex items-center justify-between gap-3 px-4 py-2.5 border-b text-sm font-medium',
            config.bg, config.text
        )}>
            <div className="flex items-center gap-2 min-w-0">
                {config.icon}
                <span>{config.message}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    onClick={onUpgrade}
                    className={cn(
                        'px-3 py-1 rounded-md text-xs font-semibold border transition-colors',
                        urgency === 'info'
                            ? 'border-[#0F2744] text-[#1D4ED8] hover:bg-[#0F2744] hover:text-white'
                            : urgency === 'warning'
                                ? 'border-[#92400E] text-[#92400E] hover:bg-[#92400E] hover:text-white'
                                : 'bg-[#991B1B] text-white border-[#991B1B] hover:bg-[#7F1D1D]'
                    )}
                >
                    {config.cta}
                </button>
                {urgency === 'info' && onDismiss && (
                    <button onClick={onDismiss} className="p-0.5 rounded hover:bg-black/10">
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}
